import { PERMISSION_CATALOG } from "./permission-catalog";

export type AppId = (typeof PERMISSION_CATALOG.app)[number];

export interface AppCatalogEntry {
  id: AppId;
  label: string;
  description: string;
  available: boolean;
}

export const APP_CATALOG: AppCatalogEntry[] = [
  {
    id: "contract-review",
    label: "Legal Contract Review",
    description:
      "Upload contracts, run AI risk analysis, and collaborate on review reports.",
    available: true,
  },
  {
    id: "drafting",
    label: "Legal Drafting Workspace",
    description: "Draft and refine legal documents with AI-assisted clause suggestions.",
    available: false,
  },
  {
    id: "regulatory",
    label: "Regulatory",
    description: "Public legal and regulatory knowledge curated by Doculegis administrators.",
    available: true,
  },
  {
    id: "rag",
    label: "KM",
    description: "Query your knowledge base and get answers from your documents.",
    available: true,
  },
  {
    id: "chat",
    label: "Chat",
    description:
      "Ask the supervisor to create and manage contract types and custom roles from one conversation.",
    available: true,
  },
  {
    id: "research",
    label: "Legal Research Support",
    description: "Research statutes, case law, and precedents with AI support.",
    available: false,
  },
];

export const APP_IDS: AppId[] = APP_CATALOG.map((entry) => entry.id);

export const DEFAULT_ENABLED_APPS: AppId[] = [...APP_IDS];

export const DEFAULT_NEW_ORG_ENABLED_APPS: AppId[] = ["contract-review", "chat"];

export function isValidAppId(value: string): value is AppId {
  return (APP_IDS as readonly string[]).includes(value);
}

export function normalizeEnabledApps(input: string[] | null | undefined): AppId[] {
  if (!input || !Array.isArray(input) || input.length === 0) {
    throw new Error("At least one application must be enabled");
  }

  const normalized: AppId[] = [];
  for (const value of input) {
    if (typeof value !== "string" || !isValidAppId(value)) {
      throw new Error(`Unknown application "${value}"`);
    }
    if (!normalized.includes(value)) {
      normalized.push(value);
    }
  }

  if (normalized.length === 0) {
    throw new Error("At least one application must be enabled");
  }

  return normalized;
}

export function parseEnabledAppsFromMetadata(
  metadata: Record<string, unknown> | null | undefined
): AppId[] {
  const raw = metadata?.enabledApps;
  if (!raw || !Array.isArray(raw)) {
    return [...DEFAULT_ENABLED_APPS];
  }

  const parsed = raw.filter((value): value is string => typeof value === "string");
  if (parsed.length === 0) {
    return [...DEFAULT_ENABLED_APPS];
  }

  try {
    return normalizeEnabledApps(parsed);
  } catch {
    return [...DEFAULT_ENABLED_APPS];
  }
}
